import APIClient from 'wootils/esm/shared/apiClient';
/**
 * Handles all the requests to the server API.
 * @extends {APIClient}
 * @ngesdoc service
 * @ngname appAPI
 * @ngmodule common
 */
class AppAPI extends APIClient {
  /**
   * @param {ngHTTP}                $http            To make the actual HTTP requests.
   * @param {ngQ}                   $q               To return failed requests with a rejected
   *                                                 promise.
   * @param {FrontendConfiguration} appConfiguration To get the API configuration.
   */
  constructor($http, $q, appConfiguration) {
    'ngInject';

    super(
      appConfiguration.api.url,
      appConfiguration.api.endpoints,
      /**
       * Send it as a callback because `this` can't be used before `super` and we need to customize
       * the implementation in order to use `$http`.
       */
      (url, options) => this._fetch(url, options),
    );

    /**
     * A local reference for the `$http` service.
     * @type {ngHTTP}
     */
    this.$http = $http;
    /**
     * A local reference for the `$q` service.
     * @type {ngQ}
     */
    this.$q = $q;
    /**
     * A local reference for the `appConfiguration` constant.
     * @type {FrontendConfiguration}
     */
    this.appConfiguration = appConfiguration;
  }
  /**
   * Returns the user information for a given token and a hash the client can use to identify
   * when the session (token) changed.
   * @param {string} token The user authentication token.
   * @return {Promise<Object,Error>}
   */
  createUserSession(token) {
    return this.get(
      this.endpoint('auth.userSession'),
      this._getAuthenticationOptions(token),
    );
  }
  /**
   * Overwrites the default `fetch` method in order to always return the `data` property of the
   * response, only on successful requests.
   * @param {Object} options         The request options.
   * @param {string} options.url     The request URL.
   * @param {string} options.method  The request method. `GET` by default.
   * @param {Object} options.body    A request body to send.
   * @param {Object} options.headers The request headers.
   * @return {Promise<Object,Error>}
   */
  fetch(options) {
    return super.fetch(options)
    .then(({ data }) => data.data);
  }
  /**
   * Get a list of the assigned accounts for a user.
   * @param {string} token       The user authentication token.
   * @param {string} [scopes=''] A list of comma separated permissions scopes to filter the
   *                             accounts.
   * @return {Promise<Object,Error>}
   */
  getAccounts(token, scopes = '') {
    return this.get(
      this.endpoint('accounts.list', { scopes }),
      this._getAuthenticationOptions(token),
    );
  }
  /**
   * Get an application information by its name.
   * @param {string} name The application name.
   * @return {Promise<Object,Error>}
   */
  getApplication(name) {
    return this.get(this.endpoint('applications.byName', { name }));
  }
  /**
   * Get the permissions a user has for an specific application.
   * @param {string} token The user authentication token.
   * @param {string} name  The application name.
   * @return {Promise<Object,Error>}
   */
  getApplicationPermissions(token, name) {
    return this.get(
      this.endpoint('permissions.application', { name }),
      this._getAuthenticationOptions(token),
    );
  }
  /**
   * Gets the information of a referrer entity used on the reset password processe. The referrer
   * entity allows the app to redirect the user back to the application where the user started the
   * process.
   * @param {string} code    The secret code to reset the password. Part of the link sent to the
   *                         user via email.
   * @param {string} refCode The referrer code. Part of the link sent to the user via email.
   * @return {Promise<Object,Error>}
   */
  getResetPasswordReferrer(code, refCode) {
    return this.get(this.endpoint('auth.referrer', {
      code,
      refCode,
    }));
  }
  /**
   * Get a permissions scope information by its name.
   * @param {string} name The name of the permission scope.
   * @return {Promise<Object,Error>}
   */
  getScope(name) {
    return this.get(this.endpoint('permissions.scope', { name }));
  }
  /**
   * Gets the list of avaiable SSO providers for a user.
   * @param {string} email The user email address.
   * @return {Promise<Object,Error>}
   */
  getSSOProviders(email) {
    return this.post(this.endpoint('auth.providers'), { email });
  }
  /**
   * Gets all the information for a given token.
   * @param {string} token The user authentication token.
   * @return {Promise<Object,Error>}
   */
  getTokenInfo(token) {
    return this.get(
      this.endpoint('auth.tokenInfo'),
      this._getAuthenticationOptions(token),
    );
  }
  /**
   * Authenticates a user using AuthNZ credentials.
   * @param {string} email    The user email address.
   * @param {string} password The user password.
   * @return {Promise<Object,Error>}
   */
  login(email, password) {
    return this.post(this.endpoint('auth.login'), { email, password });
  }
  /**
   * Requests a new password for a user.
   * @param {string}  email                The user email address.
   * @param {Object}  [options={}]         Custom options to build the referrer code, which will
   *                                       allow the user to return to the right application once
   *                                       the reset password process is finished.
   * @param {?string} options.application  An application name.
   * @param {?string} options.accountScope The name of a permissions scope.
   * @return {Promise<Object,Error>}
   */
  rememberPassword(email, options = {}) {
    return this.post(this.endpoint('auth.remember'), {
      email,
      options,
    });
  }
  /**
   * Reset a user password.
   * @param {string} code     The secret code to reset the password.
   * @param {string} password The new password.
   * @return {Promise<Object,Error>}
   */
  resetPassword(code, password) {
    return this.post(this.endpoint('auth.reset'), {
      code,
      password,
    });
  }
  /**
   * Makes the actual HTTP request using `$http`, validates if there's an error and format it
   * with the right error message.
   * @param {string} url          The request URL.
   * @param {Object} [options={}] [description]
   * @param {string} options.method  The request method. `GET` by default.
   * @param {Object} options.body    A request body to send.
   * @param {Object} options.headers The request headers.
   * @return {Promise<Object,Error>}
   * @access protected
   * @ignore
   */
  _fetch(url, options = {}) {
    // `$http` doesn't work like `fetch`, so put everything on a single object.
    const req = Object.assign({}, { url }, options);
    // If there's a request body, format it as `data`, required by `$http`.
    const { body } = req;
    if (body) {
      delete req.body;
      req.data = body;
    }
    // Make the request.
    return this.$http(req)
    .catch((errorResponse) => {
      let newErrorResponse;
      if (
        errorResponse.data &&
        errorResponse.data.data &&
        errorResponse.data.data.error
      ) {
        // If the error uses a known format, rewrite the error object that will be returned.
        newErrorResponse = Object.assign(
          { status: errorResponse.status },
          errorResponse.data.data,
        );
      } else {
        // If the error is unknown, keep it as it is.
        newErrorResponse = errorResponse;
      }
      // Return the formatted (or not) object on a rejected promise.
      return this.$q.reject(newErrorResponse);
    });
  }
  /**
   * Generates the request option for an authenticated request.
   * @param {string} token The user authentication token.
   * @return {Object}
   * @access protected
   * @ignore
   */
  _getAuthenticationOptions(token) {
    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  }
}

export default AppAPI;
